import { db, storage, auth } from '@/lib/firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { collection, addDoc, serverTimestamp, doc, deleteDoc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { COMPRESS_IMAGES_ENDPOINT, EMBED_PORTFOLIO_ITEMS_ENDPOINT, INVALIDATE_QUERY_CACHE_ENDPOINT } from '@/config/endpoints'

/**
 * Invalidate the backend query cache so new/modified items show up immediately.
 * Fire-and-forget — failures are logged but never block the caller.
 */
export async function invalidateQueryCache(): Promise<void> {
  try {
    console.log('Invalidating query cache...')
    await fetch(INVALIDATE_QUERY_CACHE_ENDPOINT, { method: 'POST' })
    console.log('✅ Query cache invalidated')
  } catch (error) {
    console.warn('Failed to invalidate query cache:', error)
  }
}

/**
 * Get image dimensions from a File object
 */
export function getImageDimensionsFromFile(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    
    img.src = url
  })
}

/**
 * Upload a portfolio image (tattoo) to Firebase Storage
 * Uses the same secure upload method as onboarding
 */
export async function uploadPortfolioImage(
  file: File,
  artistId: string
): Promise<string> {
  console.log(`[uploadPortfolioImage] Starting upload: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: "${file.type}", artistId: ${artistId}`)
  
  try {
    // Validate file - Accept files up to 50MB, but compress if needed
    // Most smartphones now take 12MP+ photos which can be 15-40MB
    const maxSize = 50 * 1024 * 1024 // 50MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']
    
    if (file.size > maxSize) {
      const error = `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size: 50MB`
      console.error('[uploadPortfolioImage]', error)
      throw new Error(error)
    }
    
    // Check file type - allow empty type if extension is valid (iOS compatibility)
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      const error = `Invalid file type "${file.type}" for "${file.name}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC)`
      console.error('[uploadPortfolioImage]', error)
      throw new Error(error)
    }
    
    if (!hasValidExtension) {
      const error = `Invalid file extension "${fileExtension}" for "${file.name}". Allowed: ${allowedExtensions.join(', ')}`
      console.error('[uploadPortfolioImage]', error)
      throw new Error(error)
    }
    
    // Create a reference to the file location (same path as onboarding)
    const timestamp = Date.now()
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').substring(0, 100)
    const fileName = `${timestamp}-${sanitizedName}`
    const storageRef = ref(storage, `portfolio-images/${artistId}/${fileName}`)
    
    // Upload the file with metadata
    // Normalize content type for HEIC files or empty types
    let contentType = file.type
    if (!contentType || contentType === '') {
      // Infer from extension if type is empty
      const ext = fileExtension.toLowerCase()
      if (ext === '.heic' || ext === '.heif') {
        contentType = 'image/heic'
      } else if (ext === '.jpg' || ext === '.jpeg') {
        contentType = 'image/jpeg'
      } else if (ext === '.png') {
        contentType = 'image/png'
      } else if (ext === '.webp') {
        contentType = 'image/webp'
      } else if (ext === '.gif') {
        contentType = 'image/gif'
      } else {
        contentType = 'image/jpeg' // fallback
      }
    }
    
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        uploadedBy: artistId,
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    }
    
    console.log(`[uploadPortfolioImage] Uploading to Firebase Storage: ${storageRef.fullPath}`)
    await uploadBytes(storageRef, file, metadata)
    
    const publicUrl = await getDownloadURL(storageRef)
    console.log(`[uploadPortfolioImage] ✅ Upload successful: ${publicUrl.substring(0, 100)}...`)
    return publicUrl
  } catch (error) {
    console.error('[uploadPortfolioImage] ❌ Error uploading portfolio image:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error(`Failed to upload portfolio image: ${String(error)}`)
  }
}

/**
 * Upload a flash image to Firebase Storage
 * Uses secure validation like portfolio uploads
 */
export async function uploadFlashImage(
  file: File,
  artistId: string
): Promise<string> {
  console.log(`[uploadFlashImage] Starting upload: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: "${file.type}", artistId: ${artistId}`)
  
  try {
    // Validate file - Accept files up to 50MB, but compress if needed
    // Most smartphones now take 12MP+ photos which can be 15-40MB
    const maxSize = 50 * 1024 * 1024 // 50MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']
    
    if (file.size > maxSize) {
      const error = `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size: 50MB`
      console.error('[uploadFlashImage]', error)
      throw new Error(error)
    }
    
    // Check file type - allow empty type if extension is valid (iOS compatibility)
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      const error = `Invalid file type "${file.type}" for "${file.name}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC)`
      console.error('[uploadFlashImage]', error)
      throw new Error(error)
    }
    
    if (!hasValidExtension) {
      const error = `Invalid file extension "${fileExtension}" for "${file.name}". Allowed: ${allowedExtensions.join(', ')}`
      console.error('[uploadFlashImage]', error)
      throw new Error(error)
    }
    
    // Create a reference to the file location
    const timestamp = Date.now()
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').substring(0, 100)
    const fileName = `${timestamp}-${sanitizedName}`
    const storageRef = ref(storage, `flashes/${artistId}/${fileName}`)
    
    // Upload the file with metadata
    // Normalize content type for HEIC files or empty types
    let contentType = file.type
    if (!contentType || contentType === '') {
      // Infer from extension if type is empty
      const ext = fileExtension.toLowerCase()
      if (ext === '.heic' || ext === '.heif') {
        contentType = 'image/heic'
      } else if (ext === '.jpg' || ext === '.jpeg') {
        contentType = 'image/jpeg'
      } else if (ext === '.png') {
        contentType = 'image/png'
      } else if (ext === '.webp') {
        contentType = 'image/webp'
      } else if (ext === '.gif') {
        contentType = 'image/gif'
      } else {
        contentType = 'image/jpeg' // fallback
      }
    }
    
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        uploadedBy: artistId,
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    }
    
    console.log(`[uploadFlashImage] Uploading to Firebase Storage: ${storageRef.fullPath}`)
    await uploadBytes(storageRef, file, metadata)
    
    const publicUrl = await getDownloadURL(storageRef)
    console.log(`[uploadFlashImage] ✅ Upload successful: ${publicUrl.substring(0, 100)}...`)
    return publicUrl
  } catch (error) {
    console.error('[uploadFlashImage] ❌ Error uploading flash image:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error(`Failed to upload flash image: ${String(error)}`)
  }
}

/**
 * Create a portfolio item document in Firestore
 */
export async function createPortfolioItem(
  artistId: string,
  imageUrl: string,
  storagePath: string,
  width: number,
  height: number
): Promise<string> {
  console.log(`[createPortfolioItem] Creating Firestore document for artist: ${artistId}`)
  
  try {
    // Debug: Check current user and their permissions
    const currentUser = auth.currentUser
    if (!currentUser) {
      const error = 'You must be logged in to create portfolio items. Please refresh the page and try again.'
      console.error('[createPortfolioItem]', error)
      throw new Error(error)
    }
    
    // Check if user is an admin
    const adminEmails = ['jari@getmoretats.com', 'keijo@getmoretats.com', 'lotta@getmoretats.com']
    const isAdmin = currentUser.email && adminEmails.includes(currentUser.email)
    
    // Debug: Check user document
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
    if (!userDoc.exists()) {
      throw new Error('User document not found. Please contact support.')
    }
    
    const userData = userDoc.data()
    console.log('Debug - Creating portfolio item:', {
      currentUserId: currentUser.uid,
      currentUserEmail: currentUser.email,
      isAdmin,
      targetArtistId: artistId,
      userData: {
        isArtist: userData?.isArtist,
        artistId: userData?.artistId,
      }
    })
    
    // Check permissions: either admin OR artist owner
    if (!isAdmin) {
      // If not admin, check if user is the artist owner
      if (!userData?.isArtist) {
        throw new Error('Your account is not registered as an artist. Please contact support.')
      }
      
      if (userData.artistId !== artistId) {
        throw new Error(`Permission denied: Your artist ID (${userData.artistId}) does not match the target artist ID (${artistId}).`)
      }
    } else {
      console.log('✅ Admin access granted for portfolio item creation')
    }
    
    const portfolioItemData = {
      artistId,
      imageUrl,
      storagePath,
      type: 'image' as const,
      width,
      height,
      createdAt: serverTimestamp(),
      isFlash: false,
    }
    
    console.log('[createPortfolioItem] Writing to Firestore...')
    const docRef = await addDoc(collection(db, 'portfolioItems'), portfolioItemData)
    console.log(`[createPortfolioItem] ✅ Portfolio item created with ID: ${docRef.id}`)
    return docRef.id
  } catch (error) {
    console.error('[createPortfolioItem] ❌ Error creating portfolio item:', error)
    if (error instanceof Error) {
      // Enhance Firebase permission errors with more helpful messages
      if (error.message.includes('permission') || error.message.includes('PERMISSION_DENIED')) {
        throw new Error(`Permission denied: Your account may not have artist privileges. Please contact support. (Original error: ${error.message})`)
      }
      throw error
    }
    throw new Error(`Failed to create portfolio item in database: ${String(error)}`)
  }
}

/**
 * Create a flash document in Firestore with correct format
 */
export async function createFlash(
  artistId: string,
  imageUrl: string,
  minPrice: string,
  minSize: string,
  eventId?: string
): Promise<string> {
  console.log(`[createFlash] Creating Firestore document for artist: ${artistId}, price: ${minPrice}, size: ${minSize}${eventId ? `, eventId: ${eventId}` : ''}`)
  
  try {
    // Debug: Check current user and their permissions
    const currentUser = auth.currentUser
    if (!currentUser) {
      const error = 'You must be logged in to create flashes. Please refresh the page and try again.'
      console.error('[createFlash]', error)
      throw new Error(error)
    }
    
    // Check if user is an admin
    const adminEmails = ['jari@getmoretats.com', 'keijo@getmoretats.com', 'lotta@getmoretats.com']
    const isAdmin = currentUser.email && adminEmails.includes(currentUser.email)
    
    // Debug: Check user document
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
    if (!userDoc.exists()) {
      throw new Error('User document not found. Please contact support.')
    }
    
    const userData = userDoc.data()
    console.log('Debug - Creating flash:', {
      currentUserId: currentUser.uid,
      currentUserEmail: currentUser.email,
      isAdmin,
      targetArtistId: artistId,
      userData: {
        isArtist: userData?.isArtist,
        artistId: userData?.artistId,
      }
    })
    
    // Check permissions: either admin OR artist owner
    if (!isAdmin) {
      // If not admin, check if user is the artist owner
      if (!userData?.isArtist) {
        throw new Error('Your account is not registered as an artist. Please contact support.')
      }
      
      if (userData.artistId !== artistId) {
        throw new Error(`Permission denied: Your artist ID (${userData.artistId}) does not match the target artist ID (${artistId}).`)
      }
    } else {
      console.log('✅ Admin access granted')
    }
    
    // Use setDoc with a generated ID instead of addDoc for better permissions handling
    const flashId = `${artistId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const flashRef = doc(db, 'flashes', flashId)
    
    // Note: Both flashes and portfolioItems use 'imageUrl' field in Firestore
    const flashData: any = {
      artistId,
      imageUrl,
      compressedImageUrl: '', // Will be filled by compression endpoint asynchronously
      minPrice,
      minSize,
      available: true,
      booked: false,
      createdAt: serverTimestamp(),
    }
    
    // Add event fields if provided
    if (eventId) {
      flashData.eventExclusive = true
      flashData.eventId = eventId
    }
    
    console.log('[createFlash] Writing to Firestore...')
    await setDoc(flashRef, flashData)
    console.log(`[createFlash] ✅ Flash created with ID: ${flashId}`)
    
    // Trigger compression endpoint asynchronously (no need to wait for response)
    try {
      fetch(COMPRESS_IMAGES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }).catch(err => console.warn('[createFlash] Compression trigger failed:', err))
    } catch (compressionError) {
      console.warn('[createFlash] Error triggering compression endpoint:', compressionError)
    }
    
    return flashId
  } catch (error) {
    console.error('[createFlash] ❌ Error creating flash:', error)
    if (error instanceof Error) {
      // Enhance Firebase permission errors with more helpful messages
      if (error.message.includes('permission') || error.message.includes('PERMISSION_DENIED')) {
        throw new Error(`Permission denied: Your account may not have artist privileges. Please contact support. (Original error: ${error.message})`)
      }
      throw error
    }
    throw new Error(`Failed to create flash in database: ${String(error)}`)
  }
}

/**
 * Upload and create a portfolio item (complete flow)
 */
export async function uploadAndCreatePortfolioItem(
  file: File,
  artistId: string
): Promise<string> {
  try {
    // Get image dimensions
    const { width, height } = await getImageDimensionsFromFile(file)
    
    // Upload image to Storage
    const imageUrl = await uploadPortfolioImage(file, artistId)
    
    // Create Firestore document
    const storagePath = `portfolio-images/${artistId}/${file.name}`
    const docId = await createPortfolioItem(artistId, imageUrl, storagePath, width, height)
    
    return docId
  } catch (error) {
    console.error('Error in portfolio item upload flow:', error)
    throw error
  }
}

/**
 * Upload and create a flash (complete flow)
 */
export async function uploadAndCreateFlash(
  file: File,
  artistId: string,
  minPrice: string,
  minSize: string,
  eventId?: string
): Promise<string> {
  try {
    // Upload image to Storage
    const imageUrl = await uploadFlashImage(file, artistId)
    
    // Create Firestore document with compression
    const docId = await createFlash(artistId, imageUrl, minPrice, minSize, eventId)
    
    return docId
  } catch (error) {
    console.error('Error in flash upload flow:', error)
    throw error
  }
}

/**
 * Upload multiple portfolio items
 */
export async function uploadMultiplePortfolioItems(
  files: File[],
  artistId: string,
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> {
  const docIds: string[] = []
  
  for (let i = 0; i < files.length; i++) {
    try {
      const docId = await uploadAndCreatePortfolioItem(files[i], artistId)
      docIds.push(docId)
      onProgress?.(i + 1, files.length)
    } catch (error) {
      console.error(`Failed to upload file ${i + 1}:`, error)
      // Continue with other files even if one fails
    }
  }
  
  // Trigger compression endpoint asynchronously (no need to wait for response)
  if (docIds.length > 0) {
    try {
      console.log('Triggering compression for portfolio items...')
      fetch(COMPRESS_IMAGES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }).catch(err => console.warn('Compression trigger failed:', err))
      
      // Trigger embedding endpoint for the uploaded portfolio items
      console.log('Triggering embedding for portfolio items:', docIds)
      fetch(EMBED_PORTFOLIO_ITEMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_ids: docIds
        }),
      }).catch(err => console.warn('Embedding trigger failed:', err))
      
      try {
        await invalidateQueryCache()
      } catch (err) {
        console.warn('Cache invalidation failed:', err)
      }
    } catch (error) {
      console.warn('Error triggering post-upload endpoints:', error)
    }
  }
  
  return docIds
}

/**
 * Upload multiple flashes
 */
export async function uploadMultipleFlashes(
  files: File[],
  artistId: string,
  minPrice: string,
  minSize: string,
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> {
  const docIds: string[] = []
  
  for (let i = 0; i < files.length; i++) {
    try {
      const docId = await uploadAndCreateFlash(files[i], artistId, minPrice, minSize)
      docIds.push(docId)
      onProgress?.(i + 1, files.length)
    } catch (error) {
      console.error(`Failed to upload file ${i + 1}:`, error)
      // Continue with other files even if one fails
    }
  }
  
  // Reset query_tattoos cache so the next query is not served stale IDs (must finish before UI refetch)
  if (docIds.length > 0) {
    try {
      await invalidateQueryCache()
    } catch (err) {
      console.warn('Cache invalidation failed:', err)
    }
  }

  return docIds
}

/**
 * Convert a data URL to a Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  
  return new Blob([u8arr], { type: mime })
}

/**
 * Upload flash from data URL (for cropped images)
 */
export async function uploadFlashFromDataUrl(
  dataUrl: string,
  artistId: string,
  minPrice: string,
  minSize: string,
  eventId?: string
): Promise<string> {
  console.log('[uploadFlashFromDataUrl] Starting upload')
  console.log('[uploadFlashFromDataUrl] Artist ID:', artistId)
  console.log('[uploadFlashFromDataUrl] Price:', minPrice, 'Size:', minSize)
  console.log('[uploadFlashFromDataUrl] Data URL length:', dataUrl.length)
  console.log('[uploadFlashFromDataUrl] Data URL prefix:', dataUrl.substring(0, 50))
  
  try {
    // Convert data URL to blob
    console.log('[uploadFlashFromDataUrl] Converting data URL to blob...')
    const blob = dataUrlToBlob(dataUrl)
    console.log('[uploadFlashFromDataUrl] Blob created, size:', blob.size, 'type:', blob.type)
    
    // Create a reference to the file location
    const timestamp = Date.now()
    const fileName = `${timestamp}-cropped.jpg`
    const storagePath = `flashes/${artistId}/${fileName}`
    console.log('[uploadFlashFromDataUrl] Storage path:', storagePath)
    
    const storageRef = ref(storage, storagePath)
    
    // Upload the blob with metadata
    const metadata = {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        uploadedBy: artistId,
        uploadedAt: new Date().toISOString(),
        cropped: 'true'
      }
    }
    
    console.log('[uploadFlashFromDataUrl] Uploading to Firebase Storage...')
    await uploadBytes(storageRef, blob, metadata)
    console.log('[uploadFlashFromDataUrl] ✅ Upload to storage successful')
    
    const imageUrl = await getDownloadURL(storageRef)
    console.log('[uploadFlashFromDataUrl] Public URL:', imageUrl.substring(0, 100) + '...')
    
    // Create Firestore document with compression
    console.log('[uploadFlashFromDataUrl] Creating Firestore document...')
    const docId = await createFlash(artistId, imageUrl, minPrice, minSize, eventId)
    console.log('[uploadFlashFromDataUrl] ✅ Flash document created with ID:', docId)
    
    // Reset cache so new flash shows up immediately
    invalidateQueryCache().catch(err => console.warn('Cache invalidation failed:', err))
    
    return docId
  } catch (error) {
    console.error('[uploadFlashFromDataUrl] ❌ Error uploading flash from data URL:', error)
    if (error instanceof Error) {
      console.error('[uploadFlashFromDataUrl] Error message:', error.message)
      console.error('[uploadFlashFromDataUrl] Error stack:', error.stack)
    }
    throw error instanceof Error ? error : new Error('Failed to upload flash from data URL')
  }
}

/**
 * Archive a portfolio item by moving it to portfolioItemsArchived collection
 */
export async function deletePortfolioItem(itemId: string): Promise<void> {
  try {
    // Get the original document
    const itemRef = doc(db, 'portfolioItems', itemId)
    const itemSnap = await getDoc(itemRef)
    
    if (!itemSnap.exists()) {
      throw new Error('Portfolio item not found')
    }
    
    // Copy to archived collection with status
    const archivedData = {
      ...itemSnap.data(),
      status: 'artistDeleted',
      archivedAt: serverTimestamp(),
      originalId: itemId
    }
    
    await setDoc(doc(db, 'portfolioItemsArchived', itemId), archivedData)
    console.log('Portfolio item archived:', itemId)
    
    // Delete from original collection
    await deleteDoc(itemRef)
    console.log('Portfolio item removed from active collection')

    invalidateQueryCache().catch((err) => console.warn('Cache invalidation failed:', err))
    
    // Note: We don't delete from Storage to preserve the image
  } catch (error) {
    console.error('Error archiving portfolio item:', error)
    throw error instanceof Error ? error : new Error('Failed to archive portfolio item')
  }
}

/**
 * Create a FOTW (Flash of the Week) flash document in Firestore
 */
export async function createFotwFlash(
  artistId: string,
  imageUrl: string,
  minPrice: string,
  minSize: string,
  fotwWeek: number
): Promise<string> {
  try {
    // Check current user and their permissions
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('You must be logged in to create FOTW flashes')
    }
    
    // Check if user is an admin
    const adminEmails = ['jari@getmoretats.com', 'keijo@getmoretats.com', 'lotta@getmoretats.com']
    const isAdmin = currentUser.email && adminEmails.includes(currentUser.email)
    
    if (!isAdmin) {
      throw new Error('Only admins can create FOTW flashes')
    }
    
    console.log('✅ Admin access granted for FOTW flash creation')
    
    // Use setDoc with a generated ID
    const artistPrefix = artistId && artistId.trim() !== '' ? artistId : 'unassigned'
    const flashId = `${artistPrefix}_fotw_${fotwWeek}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const flashRef = doc(db, 'flashes', flashId)
    
    // Create flash data with FOTW-specific fields
    // Only include optional fields if they have values
    const flashData: any = {
      imageUrl,
      compressedImageUrl: '', // Will be filled by compression endpoint asynchronously
      minPrice,
      available: true,
      booked: false,
      fotw: true,
      fotwWeek,
      votes: [],
      createdAt: serverTimestamp(),
    }
    
    // Only add artistId if it's provided and not empty
    if (artistId && artistId.trim() !== '') {
      flashData.artistId = artistId
    }
    
    // Only add minSize if it's provided and not empty
    if (minSize && minSize.trim() !== '') {
      flashData.minSize = minSize
    }
    
    await setDoc(flashRef, flashData)
    console.log('FOTW flash created:', flashId)
    
    // Trigger compression endpoint asynchronously
    try {
      fetch(COMPRESS_IMAGES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }).catch(err => console.warn('Compression trigger failed:', err))
    } catch (compressionError) {
      console.warn('Error triggering compression endpoint:', compressionError)
    }
    
    return flashId
  } catch (error) {
    console.error('Error creating FOTW flash:', error)
    throw error instanceof Error ? error : new Error('Failed to create FOTW flash')
  }
}

/**
 * Upload FOTW flash file and create flash document
 */
export async function uploadAndCreateFotwFlash(
  file: File,
  artistId: string,
  minPrice: string,
  minSize: string,
  fotwWeek: number
): Promise<string> {
  try {
    // Validate file - Accept files up to 50MB, but compress if needed
    // Most smartphones now take 12MP+ photos which can be 15-40MB
    const maxSize = 50 * 1024 * 1024 // 50MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']
    
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size: 50MB`)
    }
    
    // Check file type - allow empty type if extension is valid (iOS compatibility)
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      throw new Error(`Invalid file type "${file.type}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC)`)
    }
    
    if (!hasValidExtension) {
      throw new Error(`Invalid file extension "${fileExtension}". Please use: ${allowedExtensions.join(', ')}`)
    }
    
    // Create a reference to the file location
    const timestamp = Date.now()
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').substring(0, 100)
    const artistPrefix = artistId && artistId.trim() !== '' ? artistId : 'unassigned'
    const fileName = `${timestamp}-fotw-week${fotwWeek}-${sanitizedName}`
    const storageRef = ref(storage, `flashes/${artistPrefix}/${fileName}`)
    
    // Upload the file with metadata
    // Normalize content type for HEIC files or empty types
    let contentType = file.type
    if (!contentType || contentType === '') {
      // Infer from extension if type is empty
      const ext = fileExtension.toLowerCase()
      if (ext === '.heic' || ext === '.heif') {
        contentType = 'image/heic'
      } else if (ext === '.jpg' || ext === '.jpeg') {
        contentType = 'image/jpeg'
      } else if (ext === '.png') {
        contentType = 'image/png'
      } else if (ext === '.webp') {
        contentType = 'image/webp'
      } else if (ext === '.gif') {
        contentType = 'image/gif'
      } else {
        contentType = 'image/jpeg' // fallback
      }
    }
    
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        uploadedBy: artistPrefix,
        uploadedAt: new Date().toISOString(),
        fotw: 'true',
        fotwWeek: fotwWeek.toString()
      }
    }
    
    await uploadBytes(storageRef, file, metadata)
    
    const imageUrl = await getDownloadURL(storageRef)
    
    // Create Firestore document with FOTW fields
    const docId = await createFotwFlash(artistId, imageUrl, minPrice, minSize, fotwWeek)
    
    // Reset cache so new FOTW flash shows up immediately
    invalidateQueryCache().catch(err => console.warn('Cache invalidation failed:', err))
    
    return docId
  } catch (error) {
    console.error('Error uploading FOTW flash:', error)
    throw error instanceof Error ? error : new Error('Failed to upload FOTW flash')
  }
}

/**
 * Upload a healed portfolio image to Firebase Storage
 */
export async function uploadHealedImage(
  file: File,
  artistId: string
): Promise<string> {
  console.log(`[uploadHealedImage] Starting upload: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: "${file.type}", artistId: ${artistId}`)
  
  try {
    // Validate file - Accept files up to 50MB, but compress if needed
    // Most smartphones now take 12MP+ photos which can be 15-40MB
    const maxSize = 50 * 1024 * 1024 // 50MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']
    
    if (file.size > maxSize) {
      const error = `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size: 50MB`
      console.error('[uploadHealedImage]', error)
      throw new Error(error)
    }
    
    // Check file type - allow empty type if extension is valid (iOS compatibility)
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = file.type === '' || allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      const error = `Invalid file type "${file.type}" for "${file.name}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC)`
      console.error('[uploadHealedImage]', error)
      throw new Error(error)
    }
    
    if (!hasValidExtension) {
      const error = `Invalid file extension "${fileExtension}" for "${file.name}". Allowed: ${allowedExtensions.join(', ')}`
      console.error('[uploadHealedImage]', error)
      throw new Error(error)
    }
    
    // Create a reference to the file location
    const timestamp = Date.now()
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').substring(0, 100)
    const fileName = `${timestamp}-${sanitizedName}`
    const storageRef = ref(storage, `healed-portfolio-images/${artistId}/${fileName}`)
    
    // Upload the file with metadata
    // Normalize content type for HEIC files or empty types
    let contentType = file.type
    if (!contentType || contentType === '') {
      // Infer from extension if type is empty
      const ext = fileExtension.toLowerCase()
      if (ext === '.heic' || ext === '.heif') {
        contentType = 'image/heic'
      } else if (ext === '.jpg' || ext === '.jpeg') {
        contentType = 'image/jpeg'
      } else if (ext === '.png') {
        contentType = 'image/png'
      } else if (ext === '.webp') {
        contentType = 'image/webp'
      } else if (ext === '.gif') {
        contentType = 'image/gif'
      } else {
        contentType = 'image/jpeg' // fallback
      }
    }
    
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        uploadedBy: artistId,
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    }
    
    console.log(`[uploadHealedImage] Uploading to Firebase Storage: ${storageRef.fullPath}`)
    await uploadBytes(storageRef, file, metadata)
    
    const publicUrl = await getDownloadURL(storageRef)
    console.log(`[uploadHealedImage] ✅ Upload successful: ${publicUrl.substring(0, 100)}...`)
    return publicUrl
  } catch (error) {
    console.error('[uploadHealedImage] ❌ Error uploading healed image:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error(`Failed to upload healed image: ${String(error)}`)
  }
}

/**
 * Create a healed portfolio item document in Firestore
 */
export async function createHealedPortfolioItem(
  artistId: string,
  imageUrl: string,
  storagePath: string,
  width: number,
  height: number
): Promise<string> {
  console.log(`[createHealedPortfolioItem] Creating Firestore document for artist: ${artistId}`)
  
  try {
    // Debug: Check current user and their permissions
    const currentUser = auth.currentUser
    if (!currentUser) {
      const error = 'You must be logged in to create healed portfolio items. Please refresh the page and try again.'
      console.error('[createHealedPortfolioItem]', error)
      throw new Error(error)
    }
    
    // Check if user is an admin
    const adminEmails = ['jari@getmoretats.com', 'keijo@getmoretats.com', 'lotta@getmoretats.com']
    const isAdmin = currentUser.email && adminEmails.includes(currentUser.email)
    
    // Debug: Check user document
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
    if (!userDoc.exists()) {
      throw new Error('User document not found. Please contact support.')
    }
    
    const userData = userDoc.data()
    console.log('Debug - Creating healed portfolio item:', {
      currentUserId: currentUser.uid,
      currentUserEmail: currentUser.email,
      isAdmin,
      targetArtistId: artistId,
      userData: {
        isArtist: userData?.isArtist,
        artistId: userData?.artistId,
      }
    })
    
    // Check permissions: either admin OR artist owner
    if (!isAdmin) {
      // If not admin, check if user is the artist owner
      if (!userData?.isArtist) {
        throw new Error('Your account is not registered as an artist. Please contact support.')
      }
      
      if (userData.artistId !== artistId) {
        throw new Error(`Permission denied: Your artist ID (${userData.artistId}) does not match the target artist ID (${artistId}).`)
      }
    } else {
      console.log('✅ Admin access granted for healed portfolio item creation')
    }
    
    const healedItemData = {
      artistId,
      imageUrl,
      storagePath,
      type: 'image' as const,
      width,
      height,
      createdAt: serverTimestamp(),
      isHealed: true,
    }
    
    console.log('[createHealedPortfolioItem] Writing to Firestore...')
    const docRef = await addDoc(collection(db, 'healedPortfolioItems'), healedItemData)
    console.log(`[createHealedPortfolioItem] ✅ Healed portfolio item created with ID: ${docRef.id}`)
    return docRef.id
  } catch (error) {
    console.error('[createHealedPortfolioItem] ❌ Error creating healed portfolio item:', error)
    if (error instanceof Error) {
      // Enhance Firebase permission errors with more helpful messages
      if (error.message.includes('permission') || error.message.includes('PERMISSION_DENIED')) {
        throw new Error(`Permission denied: Your account may not have artist privileges. Please contact support. (Original error: ${error.message})`)
      }
      throw error
    }
    throw new Error(`Failed to create healed portfolio item in database: ${String(error)}`)
  }
}

/**
 * Upload and create a healed portfolio item (complete flow)
 */
export async function uploadAndCreateHealedPortfolioItem(
  file: File,
  artistId: string
): Promise<string> {
  try {
    // Get image dimensions
    const { width, height } = await getImageDimensionsFromFile(file)
    
    // Upload image to Storage (separate healed-portfolio-images folder)
    const imageUrl = await uploadHealedImage(file, artistId)
    
    // Create Firestore document in HealedPortfolioItems collection
    const storagePath = `healed-portfolio-images/${artistId}/${file.name}`
    const docId = await createHealedPortfolioItem(artistId, imageUrl, storagePath, width, height)
    
    return docId
  } catch (error) {
    console.error('Error in healed portfolio item upload flow:', error)
    throw error
  }
}

/**
 * Upload multiple healed portfolio items
 */
export async function uploadMultipleHealedPortfolioItems(
  files: File[],
  artistId: string,
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> {
  const docIds: string[] = []
  
  for (let i = 0; i < files.length; i++) {
    try {
      const docId = await uploadAndCreateHealedPortfolioItem(files[i], artistId)
      docIds.push(docId)
      onProgress?.(i + 1, files.length)
    } catch (error) {
      console.error(`Failed to upload healed item ${i + 1}:`, error)
      // Continue with other files even if one fails
    }
  }
  
  // Reset query_tattoos cache so new items can be fetched if needed
  if (docIds.length > 0) {
    try {
      await invalidateQueryCache()
    } catch (err) {
      console.warn('Cache invalidation failed:', err)
    }
  }
  
  return docIds
}

/**
 * Update flash metadata (price and size)
 */
export async function updateFlashMetadata(
  flashId: string,
  updates: {
    minPrice?: string
    minSize?: string
    booked?: boolean
  }
): Promise<void> {
  console.log('[updateFlashMetadata] Starting update:', { flashId, updates })
  
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('You must be logged in to update flashes')
    }

    console.log('[updateFlashMetadata] Current user:', currentUser.email)

    // Check if user is an admin
    const adminEmails = ['jari@getmoretats.com', 'keijo@getmoretats.com', 'lotta@getmoretats.com']
    const isAdmin = currentUser.email && adminEmails.includes(currentUser.email)

    console.log('[updateFlashMetadata] Is admin:', isAdmin)

    // Get flash to verify ownership - try main collection first
    let flashRef = doc(db, 'flashes', flashId)
    let flashSnap = await getDoc(flashRef)
    let isArchivedFlash = false
    
    // If not found in main collection, try archived
    if (!flashSnap.exists()) {
      console.log('[updateFlashMetadata] Flash not found in main collection, trying archived...')
      flashRef = doc(db, 'flashesArchived', flashId)
      flashSnap = await getDoc(flashRef)
      isArchivedFlash = true
    }
    
    if (!flashSnap.exists()) {
      throw new Error('Flash not found')
    }

    const flashData = flashSnap.data()
    console.log('[updateFlashMetadata] Current flash data:', {
      id: flashId,
      artistId: flashData.artistId,
      currentPrice: flashData.minPrice,
      currentSize: flashData.minSize,
      isArchived: isArchivedFlash
    })

    if (!isAdmin) {
      // Check user document for artist permissions
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
      if (!userDoc.exists()) {
        throw new Error('User document not found')
      }

      const userData = userDoc.data()
      console.log('[updateFlashMetadata] User data:', {
        isArtist: userData?.isArtist,
        userArtistId: userData?.artistId,
        flashArtistId: flashData.artistId
      })
      
      if (!userData?.isArtist) {
        throw new Error('Your account is not registered as an artist')
      }

      if (userData.artistId !== flashData.artistId) {
        console.warn('[updateFlashMetadata] Artist ID mismatch', {
          userArtistId: userData.artistId,
          flashArtistId: flashData.artistId,
          isArchived: isArchivedFlash
        })
        // For testing archived flashes, allow if flash is archived
        // TODO: Remove this after testing - this is a temporary workaround for testing archived flashes
        if (!isArchivedFlash) {
          throw new Error('Permission denied: You can only update your own flashes')
        }
        console.log('[updateFlashMetadata] Allowing update for archived flash during testing')
      }
    }

    // Prepare update object with only defined fields
    const updateData: any = {}
    if (updates.minPrice !== undefined) {
      updateData.minPrice = updates.minPrice
    }
    if (updates.minSize !== undefined) {
      updateData.minSize = updates.minSize
    }
    if (updates.booked !== undefined) {
      updateData.booked = updates.booked
    }

    console.log('[updateFlashMetadata] Updating Firestore with:', updateData)
    await updateDoc(flashRef, updateData)
    console.log('[updateFlashMetadata] ✅ Successfully updated flash metadata:', flashId)
  } catch (error) {
    console.error('[updateFlashMetadata] ❌ Error updating flash metadata:', error)
    throw error instanceof Error ? error : new Error('Failed to update flash metadata')
  }
}

/**
 * Archive a flash by moving it to flashesArchived collection
 */
export async function deleteFlash(flashId: string): Promise<void> {
  try {
    // Get the original document
    const flashRef = doc(db, 'flashes', flashId)
    const flashSnap = await getDoc(flashRef)
    
    if (!flashSnap.exists()) {
      throw new Error('Flash not found')
    }
    
    // Copy to archived collection with status
    const archivedData = {
      ...flashSnap.data(),
      status: 'artistDeleted',
      archivedAt: serverTimestamp(),
      originalId: flashId
    }
    
    await setDoc(doc(db, 'flashesArchived', flashId), archivedData)
    console.log('Flash archived:', flashId)
    
    // Delete from original collection
    await deleteDoc(flashRef)
    console.log('Flash removed from active collection')

    invalidateQueryCache().catch((err) => console.warn('Cache invalidation failed:', err))
    
    // Note: We don't delete from Storage to preserve the image
  } catch (error) {
    console.error('Error archiving flash:', error)
    throw error instanceof Error ? error : new Error('Failed to archive flash')
  }
}

/**
 * Delete a healed portfolio item (moves to archived collection)
 */
export async function deleteHealedPortfolioItem(itemId: string): Promise<void> {
  try {
    // Get the original document
    const itemRef = doc(db, 'healedPortfolioItems', itemId)
    const itemSnap = await getDoc(itemRef)
    
    if (!itemSnap.exists()) {
      throw new Error('Healed portfolio item not found')
    }
    
    // Copy to archived collection with status
    const archivedData = {
      ...itemSnap.data(),
      status: 'artistDeleted',
      archivedAt: serverTimestamp(),
      originalId: itemId
    }
    
    await setDoc(doc(db, 'healedPortfolioItemsArchived', itemId), archivedData)
    console.log('Healed portfolio item archived:', itemId)
    
    // Delete from original collection
    await deleteDoc(itemRef)
    console.log('Healed portfolio item removed from active collection')

    invalidateQueryCache().catch((err) => console.warn('Cache invalidation failed:', err))
    
    // Note: We don't delete from Storage to preserve the image
  } catch (error) {
    console.error('Error archiving healed portfolio item:', error)
    throw error instanceof Error ? error : new Error('Failed to archive healed portfolio item')
  }
}
